import {
  rolesFromAppMetadata,
  toAuthErrorCode,
  type AppRole,
  type AuthErrorCode,
} from '@platform/schema';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { supabase } from './client';

import type { Session } from '@supabase/supabase-js';

export interface AuthState {
  session: Session | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  roles: AppRole[];
  /**
   * True until the stored session has been read.
   *
   * Without it there is a moment on every page load where the admin is signed in and
   * looks signed out, and the route guard redirects to sign-in before the session
   * arrives — which reads as "it logs me out when I refresh".
   */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AuthErrorCode | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  children,
  onSignOut,
}: {
  children: ReactNode;
  /** Called after the session is cleared, so cached server state goes with it. */
  onSignOut?: () => void;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    // Every event carries the current session, TOKEN_REFRESHED included, so setting
    // from it uniformly means a refresh never passes through a signed-out state that
    // the route guard would act on.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    // A code rather than the provider's string: the caller decides how to render it,
    // and Supabase's wording is not something this app should put in front of anyone.
    return error ? toAuthErrorCode(error) : null;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    // After the session, not before: if sign-out fails, the cache still matches the
    // session that is still valid.
    onSignOut?.();
  }, [onSignOut]);

  const value = useMemo<AuthState>(() => {
    const roles =
      session === null
        ? []
        : rolesFromAppMetadata(
            (session.user as { app_metadata?: unknown }).app_metadata,
          );

    return {
      session,
      isAuthenticated: session !== null,
      isAdmin: roles.includes('admin'),
      roles,
      loading,
      signIn,
      signOut,
    };
  }, [session, loading, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return value;
}
